import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Paper,
  Breadcrumbs,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Alert,
  TextField,
  AlertTitle,
  List,
  ListItem,
  ListItemText as MuiListItemText,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Description as DescriptionIcon,
  NavigateNext as NavigateNextIcon,
  Edit as EditIcon,
  RateReview as ReviewIcon,
  Upload as UploadIcon,
  Download as DownloadIcon,
  GetApp as GetAppIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
  Replay as ReplayIcon,
  SignalWifi4Bar as ConnectionIcon,
} from '@mui/icons-material';
import { hasPermission } from '../../../utils/permissions';
import { useNotification } from '../../../components/Notification';
import { formatDateWithCulture } from '../../../utils/dateUtils';
import { DEVICE_STATUS_WAITING } from '../../../constants';
import Table from '../../../components/Table';
import Input from '../../../components/Input';
import MultiSelect from '../../../components/MultiSelect';
import AddDeviceModal from './AddDeviceModal';
import ReviewDeviceModal from './ReviewDeviceModal';
import UpdateConnectionStatusModal from './UpdateConnectionStatusModal';
import {
  fetchInit,
  fetchDevices,
  fetchDeviceById,
  selectDevices,
  selectDeviceStatuses,
  selectConnectionStatuses,
  selectDeviceTypes,
  selectVerifyModes,
  selectAvailableUnits,
  selectInitDevice,
  selectCanReview,
  selectDevicesLoading,
  selectExportLoading,
  selectDevicesTotal,
  selectDevicesPage,
  selectDevicesPageSize,
  selectDevicesSortColumn,
  selectDevicesSortDirection,
  selectDevicesFilterCode,
  selectDevicesFilterStatuses,
  selectDevicesFilterUnits,
  selectDevicesError,
  selectDevicesSuccessMessage,
  selectSelectedDevice,
  setPage,
  setPageSize,
  setSortColumn,
  setSortDirection,
  setFilterCode,
  setFilterStatuses,
  setFilterUnits,
  addDevice,
  updateDevice,
  deleteDevice,
  bulkDeleteDevices,
  approveDevice,
  rejectDevice,
  revokeDevice,
  toggleLGSPAccess,
  updateConnectionStatus,
  clearError,
  clearSuccessMessage,
  clearSelectedDevice,
  importDevices,
  exportDevices,
  downloadTemplate,
  bulkReapproveDevices,
} from './slice';

function Devices() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { showNotification } = useNotification();

  const devices = useSelector(selectDevices);
  const deviceStatuses = useSelector(selectDeviceStatuses);
  const connectionStatuses = useSelector(selectConnectionStatuses);
  const deviceTypes = useSelector(selectDeviceTypes);
  const verifyModes = useSelector(selectVerifyModes);
  const availableUnits = useSelector(selectAvailableUnits);
  const initDevice = useSelector(selectInitDevice);
  const canReview = useSelector(selectCanReview);
  const loading = useSelector(selectDevicesLoading);
  const exportLoading = useSelector(selectExportLoading);
  const total = useSelector(selectDevicesTotal);
  const page = useSelector(selectDevicesPage);
  const pageSize = useSelector(selectDevicesPageSize);
  const sortColumn = useSelector(selectDevicesSortColumn);
  const sortDirection = useSelector(selectDevicesSortDirection);
  const filterCode = useSelector(selectDevicesFilterCode);
  const filterStatuses = useSelector(selectDevicesFilterStatuses);
  const filterUnits = useSelector(selectDevicesFilterUnits);
  const error = useSelector(selectDevicesError);
  const successMessage = useSelector(selectDevicesSuccessMessage);
  const selectedDevice = useSelector(selectSelectedDevice);

  const [selectedIds, setSelectedIds] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deviceToDelete, setDeviceToDelete] = useState(null);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [deviceToReview, setDeviceToReview] = useState(null);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importFileError, setImportFileError] = useState(null);
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
  const [showReapproveDialog, setShowReapproveDialog] = useState(false);
  const [reapproveReason, setReapproveReason] = useState('');
  const [deviceToReapprove, setDeviceToReapprove] = useState(null);
  const [isConnectionStatusModalOpen, setIsConnectionStatusModalOpen] = useState(false);
  const [deviceToUpdateConnectionStatus, setDeviceToUpdateConnectionStatus] = useState(null);

  // Check permissions
  const canViewDevice = hasPermission('device', 'viewDevice');
  const canAddDevice = hasPermission('device', 'addDevice');
  const canEditDevice = hasPermission('device', 'editDevice');
  const canDeleteDevice = hasPermission('device', 'deleteDevice');

  // Fetch init data on mount
  useEffect(() => {
    dispatch(fetchInit());
  }, [dispatch]);

  // Fetch devices on mount and when filters change
  useEffect(() => {
    dispatch(fetchDevices({ page, pageSize, sortColumn, sortDirection, filterCode, filterStatuses, filterUnits }));
  }, [dispatch, page, pageSize, sortColumn, sortDirection, filterCode, filterStatuses, filterUnits]);

  // Show notifications
  useEffect(() => {
    if (error) {
      showNotification(error, 'error');
      dispatch(clearError());
    }
  }, [error, showNotification, dispatch]);

  useEffect(() => {
    if (successMessage) {
      showNotification(t(successMessage), 'success');
      dispatch(clearSuccessMessage());
      // Refresh table after successful operation
      dispatch(fetchDevices({ page, pageSize, sortColumn, sortDirection, filterCode, filterStatuses, filterUnits }));
      setIsModalOpen(false);
      setIsReviewModalOpen(false);
      setSelectedIds([]);
    }
  }, [successMessage, t, showNotification, dispatch, page, pageSize, sortColumn, sortDirection, filterCode, filterStatuses, filterUnits]);

  const handleAddDevice = () => {
    // Clear previous selected device
    dispatch(clearSelectedDevice());
    // Open modal in add mode
    setEditMode(false);
    setIsModalOpen(true);
  };

  const handleEdit = (row) => {
    // Fetch device details by ID
    dispatch(fetchDeviceById(row.id));
    setEditMode(true);
    setIsModalOpen(true);
  };

  const handleDelete = (row) => {
    setDeviceToDelete(row);
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = () => {
    if (deviceToDelete?.id) {
      dispatch(deleteDevice(deviceToDelete.id));
    }
    setShowDeleteDialog(false);
    setDeviceToDelete(null);
  };

  const handleCancelDelete = () => {
    setShowDeleteDialog(false);
    setDeviceToDelete(null);
  };

  const handleBulkDelete = () => {
    setShowBulkDeleteDialog(true);
  };

  const handleConfirmBulkDelete = () => {
    dispatch(bulkDeleteDevices(selectedIds));
    setShowBulkDeleteDialog(false);
    setSelectedIds([]);
  };

  const handleCancelBulkDelete = () => {
    setShowBulkDeleteDialog(false);
  };

  const handleModalSubmit = (formData) => {
    if (editMode) {
      dispatch(updateDevice(formData));
    } else {
      dispatch(addDevice(formData));
    }
    // Close modal immediately and refresh table
    setIsModalOpen(false);
    // Refresh table after a short delay to ensure backend has processed the update
    setTimeout(() => {
      dispatch(fetchDevices({ page, pageSize, sortColumn, sortDirection, filterCode, filterStatuses, filterUnits }));
    }, 500);
  };

  const handleReview = (row) => {
    setDeviceToReview(row);
    setIsReviewModalOpen(true);
  };

  const handleApprove = (id) => {
    dispatch(approveDevice(id));
  };

  const handleReject = (id, statusNote) => {
    dispatch(rejectDevice({ id, statusNote }));
  };

  const handleRevoke = (id, statusNote) => {
    dispatch(revokeDevice({ id, statusNote }));
  };

  const handleToggleLGSPAccess = (id, allowLGSPAccess) => {
    dispatch(toggleLGSPAccess({ id, allowLGSPAccess }));
  };

  const handleImportClick = () => {
    setShowImportDialog(true);
  };

  const handleImportFileChange = (event) => {
    const file = event.target.files[0];

    // Validate file type
    if (file) {
      const fileExtension = file.name.split('.').pop().toLowerCase();
      const validExtensions = ['xlsx', 'xls'];

      if (!validExtensions.includes(fileExtension)) {
        setImportFileError(t('devices.importDialog.invalidFileType') || 'Chỉ hỗ trợ file .xlsx và .xls');
        setImportFile(null);
        return;
      }

      setImportFileError(null);
      setImportFile(file);
    }
  };

  const handleImportConfirm = () => {
    if (importFile && !importFileError) {
      dispatch(importDevices(importFile));
      setShowImportDialog(false);
      setImportFile(null);
      setImportFileError(null);
    }
  };

  const handleImportCancel = () => {
    setShowImportDialog(false);
    setImportFile(null);
    setImportFileError(null);
  };

  const handleExportClick = (event) => {
    setExportMenuAnchor(event.currentTarget);
  };

  const handleExportClose = () => {
    setExportMenuAnchor(null);
  };

  const handleExportExcel = () => {
    const filters = {
      deviceCode: filterCode,
      statuses: filterStatuses,
      units: filterUnits,
    };
    dispatch(exportDevices({ exportType: 'excel', filters, selectedIds: selectedIds.length > 0 ? selectedIds : null }));
    handleExportClose();
  };

  const handleExportPdf = () => {
    const filters = {
      deviceCode: filterCode,
      statuses: filterStatuses,
      units: filterUnits,
    };
    dispatch(exportDevices({ exportType: 'pdf', filters, selectedIds: selectedIds.length > 0 ? selectedIds : null }));
    handleExportClose();
  };

  const handleDownloadTemplate = () => {
    dispatch(downloadTemplate());
  };

  const handleReapproveSingle = (row) => {
    setDeviceToReapprove(row);
    setShowReapproveDialog(true);
    setReapproveReason('');
  };

  const handleConfirmReapprove = () => {
    if (deviceToReapprove) {
      dispatch(bulkReapproveDevices({ ids: [deviceToReapprove.id], statusNote: reapproveReason }));
    }
    setShowReapproveDialog(false);
    setReapproveReason('');
    setDeviceToReapprove(null);
  };

  const handleCancelReapprove = () => {
    setShowReapproveDialog(false);
    setReapproveReason('');
    setDeviceToReapprove(null);
  };

  const handleUpdateConnectionStatusClick = (row) => {
    setDeviceToUpdateConnectionStatus(row);
    setIsConnectionStatusModalOpen(true);
  };

  const handleUpdateConnectionStatus = (id, connectionStatus, reason) => {
    dispatch(updateConnectionStatus({ id, connectionStatus, reason }));
    setIsConnectionStatusModalOpen(false);
    setDeviceToUpdateConnectionStatus(null);
  };

  const handleCloseConnectionStatusModal = () => {
    setIsConnectionStatusModalOpen(false);
    setDeviceToUpdateConnectionStatus(null);
  };

  const columns = [
    { key: 'deviceCode', label: t('devices.table.deviceCode') },
    { key: 'name', label: t('devices.table.name') },
    {
      key: 'deviceType',
      label: t('devices.table.deviceType'),
      render: (value) => {
        const deviceTypeObj = deviceTypes.find((type) => type.id === value);
        return deviceTypeObj ? deviceTypeObj.name : '-';
      },
    },
    { key: 'model', label: t('devices.table.model') },
    { key: 'serialNumber', label: t('devices.table.serialNumber') },
    { key: 'unitName', label: t('devices.table.unitName') },
    {
      key: 'registrationDate',
      label: t('devices.table.registrationDate'),
      render: (value) => formatDateWithCulture(value, i18n.language) || '-',
    },
    {
      key: 'approvedDate',
      label: t('devices.table.approvedDate'),
      render: (value) => formatDateWithCulture(value, i18n.language) || '-',
    },
    {
      key: 'status',
      label: t('devices.table.status'),
      render: (value) => {
        const statusObj = deviceStatuses.find((status) => status.id === value);
        const statusName = statusObj ? statusObj.name : '-';
        return (
          <Tooltip title={statusName} arrow placement="top">
            <Box
              component="span"
              sx={{
                px: 1.5,
                py: 0.5,
                borderRadius: 1,
                fontSize: '0.875rem',
                fontWeight: 500,
                backgroundColor: statusObj ? '#e3f2fd' : '#e2e8f0',
                color: statusObj ? '#1565c0' : '#64748b',
              }}
            >
              {statusName}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      key: 'connectionStatus',
      label: t('devices.table.connectionStatus'),
      render: (value) => {
        const connectionStatusObj = connectionStatuses.find((status) => status.id === value);
        const statusName = connectionStatusObj ? connectionStatusObj.name : '-';
        // Color coding based on connection status
        let bgColor = '#e2e8f0'; // default gray
        let textColor = '#64748b';
        if (value === 1) { // Online
          bgColor = '#dcfce7';
          textColor = '#16a34a';
        } else if (value === 0) { // Offline
          bgColor = '#f1f5f9';
          textColor = '#64748b';
        } else if (value === 2) { // LostKey/Warning
          bgColor = '#fef3c7';
          textColor = '#d97706';
        } else if (value === 3) { // Error
          bgColor = '#fee2e2';
          textColor = '#dc2626';
        } else if (value === 4) { // Maintenance
          bgColor = '#e0e7ff';
          textColor = '#4f46e5';
        } else if (value === 5) { // Revoked
          bgColor = '#fce7f3';
          textColor = '#be185d';
        }
        return (
          <Tooltip title={statusName} arrow placement="top">
            <Box
              component="span"
              sx={{
                px: 1.5,
                py: 0.5,
                borderRadius: 1,
                fontSize: '0.875rem',
                fontWeight: 500,
                backgroundColor: bgColor,
                color: textColor,
              }}
            >
              {statusName}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      key: 'isValid',
      label: t('devices.table.isValid'),
      render: (value) => (value ? t('devices.valid.yes') : t('devices.valid.no')),
    },
    {
      key: 'allowLGSPAccess',
      label: t('devices.table.allowLGSPAccess'),
      render: (value) => (value ? t('devices.lgspAccess.yes') : t('devices.lgspAccess.no')),
    },
    { key: 'maxCallToCSDL', label: t('devices.table.maxCallToCSDL') },
    { key: 'currentCallToCSDL', label: t('devices.table.currentCallToCSDL') },
  ];

  // Custom actions renderer to include Review button
  const renderActions = (row) => (
    <>
      {canReview && (
        <IconButton
          onClick={() => handleReview(row)}
          title={t('devices.actions.review')}
          color="info"
          size="small"
          id={`devices-table-button-review-${row.id}`}
          name={`devices-table-button-review-${row.id}`}
          data-testid={`devices-table-button-review-${row.id}`}
        >
          <ReviewIcon fontSize="small" />
        </IconButton>
      )}
      {canReview && (
        <IconButton
          onClick={() => handleReapproveSingle(row)}
          title={t('devices.actions.reapprove')}
          color="warning"
          size="small"
          id={`devices-table-button-reapprove-${row.id}`}
          name={`devices-table-button-reapprove-${row.id}`}
          data-testid={`devices-table-button-reapprove-${row.id}`}
        >
          <ReplayIcon fontSize="small" />
        </IconButton>
      )}
      {canEditDevice && (
        <IconButton
          onClick={() => handleUpdateConnectionStatusClick(row)}
          title={t('devices.actions.updateConnectionStatus')}
          color="secondary"
          size="small"
          id={`devices-table-button-updateConnectionStatus-${row.id}`}
          name={`devices-table-button-updateConnectionStatus-${row.id}`}
          data-testid={`devices-table-button-updateConnectionStatus-${row.id}`}
        >
          <ConnectionIcon fontSize="small" />
        </IconButton>
      )}
      {canEditDevice && (
        <IconButton
          onClick={() => handleEdit(row)}
          title={t('devices.actions.edit')}
          color="primary"
          size="small"
          id={`devices-table-button-edit-${row.id}`}
          name={`devices-table-button-edit-${row.id}`}
          data-testid={`devices-table-button-edit-${row.id}`}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      )}
      {canDeleteDevice && (
        <IconButton
          onClick={() => handleDelete(row)}
          title={t('devices.actions.delete')}
          color="error"
          size="small"
          id={`devices-table-button-delete-${row.id}`}
          name={`devices-table-button-delete-${row.id}`}
          data-testid={`devices-table-button-delete-${row.id}`}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      )}
    </>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header Section */}
      <Box sx={{ px: 3, pt: 3, pb: 2, backgroundColor: '#ffffff' }}>
        {/* Breadcrumbs */}
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          sx={{ mb: 2 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <DescriptionIcon fontSize="small" />
            <Typography variant="body2">{t('devices.breadcrumb.deviceManagement')}</Typography>
          </Box>
          <Typography variant="body2" color="text.primary">
            {t('devices.breadcrumb.devices')}
          </Typography>
        </Breadcrumbs>

        {/* Page Title and Action Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h5" component="h1" fontWeight="bold" sx={{ textAlign: 'left' }}>
            {t('devices.title')}
          </Typography>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            {canAddDevice && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleAddDevice}
                id="addDevice"
                name="addDevice"
                data-testid="devices-button-addDevice"
              >
                {t('devices.addDevice')}
              </Button>
            )}
            {canAddDevice && (
              <Button
                variant="outlined"
                color="success"
                startIcon={<UploadIcon />}
                onClick={handleImportClick}
                id="importDevices"
                name="importDevices"
                data-testid="devices-button-importDevices"
              >
                {t('devices.import')}
              </Button>
            )}
            {canViewDevice && (
              <Button
                variant="outlined"
                color="info"
                startIcon={exportLoading ? <CircularProgress size={20} /> : <DownloadIcon />}
                onClick={handleExportClick}
                disabled={exportLoading}
                id="exportDevices"
                name="exportDevices"
                data-testid="devices-button-exportDevices"
              >
                {exportLoading ? t('devices.exporting') || 'Đang xuất...' : t('devices.export')}
              </Button>
            )}
            {canDeleteDevice && selectedIds.length > 0 && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleBulkDelete}
                id="deleteSelected"
                name="deleteSelected"
                data-testid="devices-button-deleteSelected"
              >
                {t('devices.deleteSelected')} ({selectedIds.length})
              </Button>
            )}
          </Box>
        </Box>
      </Box>

      {/* Main Content */}
      <Paper elevation={0} sx={{ borderRadius: 0, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', backgroundColor: '#ffffff', mt: 2 }}>
        {/* Filter Section */}
        <Box sx={{ px: 3, pt: 2, pb: 2, borderBottom: '1px solid #e5e7eb' }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Input
              label={t('devices.filterByCode')}
              value={filterCode}
              onChange={(e) => dispatch(setFilterCode(e.target.value))}
              placeholder={t('devices.filterByCodePlaceholder')}
              id="filterCode"
              name="filterCode"
              data-testid="devices-input-filterCode"
              sx={{ width: 320 }}
            />
            <MultiSelect
              label={t('devices.filterByStatus')}
              value={filterStatuses}
              onChange={(e) => dispatch(setFilterStatuses(e.target.value))}
              options={deviceStatuses}
              placeholder={t('devices.filterByStatusPlaceholder')}
              id="filterStatuses"
              name="filterStatuses"
              data-testid="devices-select-filterStatuses"
              sx={{ width: 320 }}
            />
            <MultiSelect
              label={t('devices.filterByUnit')}
              value={filterUnits}
              onChange={(e) => dispatch(setFilterUnits(e.target.value))}
              options={availableUnits}
              placeholder={t('devices.filterByUnitPlaceholder')}
              id="filterUnits"
              name="filterUnits"
              data-testid="devices-select-filterUnits"
              sx={{ width: 320 }}
            />
          </Box>
        </Box>

        {/* Table */}
        <Box sx={{ flex: 1, overflow: 'auto', px: 3, pt: 2, pb: 3, backgroundColor: '#ffffff' }}>
          <Table
            id="devices-table"
            columns={columns}
            data={devices}
            selectable={true}
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
            renderActions={renderActions}
            onRowDoubleClick={canEditDevice ? handleEdit : null}
            loading={loading}
            emptyMessage={t('devices.noData')}
            idField="id"
            pagination={true}
            page={page}
            pageSize={pageSize}
            totalItems={total}
            onPageChange={(newPage) => dispatch(setPage(newPage))}
            onPageSizeChange={(newSize) => dispatch(setPageSize(newSize))}
            pageSizeOptions={[5, 10, 20, 50, 100]}
            sortable={true}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSortChange={(col, dir) => {
              dispatch(setSortColumn(col));
              dispatch(setSortDirection(dir));
            }}
            sortableColumns={['deviceCode', 'name', 'deviceType', 'model', 'serialNumber', 'unitName', 'registrationDate', 'approvedDate', 'status', 'isValid', 'allowLGSPAccess']}
          />
        </Box>
      </Paper>

      {/* Confirm Delete Dialog */}
      <Dialog
        open={showDeleteDialog}
        onClose={handleCancelDelete}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: 'error.main',
          }}
        >
          <DeleteIcon />
          {t('devices.deleteDialog.title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('devices.deleteDialog.message', {
              code: deviceToDelete?.deviceCode || '',
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCancelDelete}
            variant="outlined"
            id="cancelDelete"
            name="cancelDelete"
            data-testid="devices-button-cancelDelete"
          >
            {t('devices.deleteDialog.cancel')}
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            id="confirmDelete"
            name="confirmDelete"
            data-testid="devices-button-confirmDelete"
          >
            {t('devices.deleteDialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Bulk Delete Dialog */}
      <Dialog
        open={showBulkDeleteDialog}
        onClose={handleCancelBulkDelete}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: 'error.main',
          }}
        >
          <DeleteIcon />
          {t('devices.bulkDeleteDialog.title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('devices.bulkDeleteDialog.message', {
              count: selectedIds.length,
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCancelBulkDelete}
            variant="outlined"
            id="cancelBulkDelete"
            name="cancelBulkDelete"
            data-testid="devices-button-cancelBulkDelete"
          >
            {t('devices.bulkDeleteDialog.cancel')}
          </Button>
          <Button
            onClick={handleConfirmBulkDelete}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            id="confirmBulkDelete"
            name="confirmBulkDelete"
            data-testid="devices-button-confirmBulkDelete"
          >
            {t('devices.bulkDeleteDialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Modal */}
      <AddDeviceModal
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          dispatch(clearSelectedDevice());
        }}
        onSubmit={handleModalSubmit}
        editMode={editMode}
        deviceData={selectedDevice}
        initDevice={initDevice}
        availableUnits={availableUnits}
        availableDeviceTypes={deviceTypes}
        availableVerifyModes={verifyModes}
        availableConnectionStatuses={connectionStatuses}
      />

      {/* Review Modal */}
      <ReviewDeviceModal
        open={isReviewModalOpen}
        onClose={() => setIsReviewModalOpen(false)}
        deviceData={deviceToReview}
        deviceStatuses={deviceStatuses}
        deviceTypes={deviceTypes}
        onApprove={handleApprove}
        onReject={handleReject}
        onRevoke={handleRevoke}
        onToggleLGSPAccess={handleToggleLGSPAccess}
        i18n={i18n}
      />

      {/* Update Connection Status Modal */}
      <UpdateConnectionStatusModal
        open={isConnectionStatusModalOpen}
        onClose={handleCloseConnectionStatusModal}
        deviceData={deviceToUpdateConnectionStatus}
        connectionStatuses={connectionStatuses}
        onUpdateConnectionStatus={handleUpdateConnectionStatus}
        loading={loading}
      />

      {/* Import Dialog */}
      <Dialog
        open={showImportDialog}
        onClose={handleImportCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: 'success.main',
            pb: 1,
          }}
        >
          <UploadIcon />
          {t('devices.importDialog.title')}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <DialogContentText sx={{ mb: 3 }}>
            {t('devices.importDialog.message')}
          </DialogContentText>

          {/* Import Instructions removed as per requirement */}

          {/* File Type Error */}
          {importFileError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {importFileError}
            </Alert>
          )}

          {/* Download Template Section */}
          <Box
            sx={{
              mb: 3,
              p: 2,
              backgroundColor: '#f5f5f5',
              borderRadius: 1,
              border: '1px dashed #ccc',
            }}
          >
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              {t('devices.importDialog.templateSection')}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<GetAppIcon />}
              onClick={handleDownloadTemplate}
              fullWidth
              id="downloadTemplateInDialog"
              name="downloadTemplateInDialog"
              data-testid="devices-button-downloadTemplateInDialog"
            >
              {t('devices.downloadTemplate')}
            </Button>
          </Box>

          {/* File Upload Section */}
          <Box>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              {t('devices.importDialog.uploadSection')}
            </Typography>
            <Button
              variant="outlined"
              component="label"
              fullWidth
              sx={{
                py: 1.5,
                justifyContent: 'flex-start',
                color: importFile ? 'success.main' : 'text.secondary',
                borderColor: importFile ? 'success.main' : 'divider',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <DescriptionIcon sx={{ mr: 1 }} />
                <Typography variant="body2" sx={{ flex: 1, textAlign: 'left' }}>
                  {importFile ? importFile.name : t('devices.importDialog.selectFile')}
                </Typography>
              </Box>
              <input
                type="file"
                hidden
                accept=".xlsx,.xls"
                onChange={handleImportFileChange}
                id="importFile"
                name="importFile"
                data-testid="devices-input-importFile"
              />
            </Button>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
          <Button
            onClick={handleImportCancel}
            variant="outlined"
            id="cancelImport"
            name="cancelImport"
            data-testid="devices-button-cancelImport"
          >
            {t('devices.importDialog.cancel')}
          </Button>
          <Button
            onClick={handleImportConfirm}
            variant="contained"
            color="success"
            startIcon={<UploadIcon />}
            disabled={!importFile}
            id="confirmImport"
            name="confirmImport"
            data-testid="devices-button-confirmImport"
          >
            {t('devices.importDialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Export Menu */}
      <Menu
        anchorEl={exportMenuAnchor}
        open={Boolean(exportMenuAnchor)}
        onClose={handleExportClose}
      >
        <MenuItem
          onClick={handleExportExcel}
          id="exportExcel"
          data-testid="devices-menu-exportExcel"
        >
          <ListItemIcon>
            <ExcelIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('devices.exportMenu.excel')}</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={handleExportPdf}
          id="exportPdf"
          data-testid="devices-menu-exportPdf"
        >
          <ListItemIcon>
            <PdfIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('devices.exportMenu.pdf')}</ListItemText>
        </MenuItem>
      </Menu>

      {/* Re-approve Dialog */}
      <Dialog
        open={showReapproveDialog}
        onClose={handleCancelReapprove}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: 'warning.main',
          }}
        >
          <ReplayIcon />
          {t('devices.reapproveDialog.title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t('devices.reapproveDialog.message', {
              code: deviceToReapprove?.deviceCode || '',
            })}
          </DialogContentText>
          <TextField
            label={t('devices.reapproveDialog.reasonLabel')}
            value={reapproveReason}
            onChange={(e) => setReapproveReason(e.target.value)}
            multiline
            rows={3}
            fullWidth
            required
            error={!reapproveReason.trim()}
            helperText={!reapproveReason.trim() ? t('devices.reapproveDialog.reasonRequired') : ''}
            placeholder={t('devices.reapproveDialog.reasonPlaceholder')}
            id="reapproveReason"
            name="reapproveReason"
            data-testid="devices-input-reapproveReason"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCancelReapprove}
            variant="outlined"
            id="cancelReapprove"
            name="cancelReapprove"
            data-testid="devices-button-cancelReapprove"
          >
            {t('devices.reapproveDialog.cancel')}
          </Button>
          <Button
            onClick={handleConfirmReapprove}
            variant="contained"
            color="warning"
            startIcon={<ReplayIcon />}
            disabled={!reapproveReason.trim()}
            id="confirmReapprove"
            name="confirmReapprove"
            data-testid="devices-button-confirmReapprove"
          >
            {t('devices.reapproveDialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Devices;
